import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { paginateSupabase } from '@/lib/supabase-paginate';
import { convertAsOfForAccount, RateRow } from '@/lib/fx';
import {
  classifyExpenseToPnlLine,
  PNL_COGS_LINES,
  PNL_SGA_LINES,
} from '@/components/finance/reports/pnl-report/mapping';
import { projectTrend } from './forecastMath';

const BASE_CCY = 'ETB';
const LOOKBACK = 6; // trailing complete months used to fit the revenue/cost trend
const EXP_LOOKBACK = 12; // expenses window — lets periodic costs be amortized over a full year
const LUMPY_MAX_ACTIVE_MONTHS = 7; // ≤ this many active months in 12 ⇒ treat as periodic (amortize)
const HORIZON = 3; // months projected forward (current month + next 2)
const OTHER_SGA = 'Other Operating Costs'; // operating expenses that don't map to a named line
// Grant income counted as revenue, matching the Business Unit P&L (Grantt BU).
const GRANT_INCOME_CATS = new Set(['grant', 'cost_reimbursement', 'refund']);

export type ForecastCurrency = 'ETB' | 'USD';

export type ForecastPnlRowKind = 'header' | 'sub' | 'cost' | 'total' | 'cp' | 'margin';

export interface ForecastPnlRow {
  key: string;
  label: string;
  kind: ForecastPnlRowKind;
  values: number[]; // per forecast month; margin rows carry a 0-1 ratio
  total?: number; // horizon total (blended ratio for margin rows); undefined for headers
}

export interface ForecastPnlResult {
  basisMonths: { key: string; label: string }[];
  forecastMonths: { key: string; label: string }[];
  rows: ForecastPnlRow[];
}

interface CategoryNode {
  name: string;
  parent_id: string | null;
  category_type: string | null;
  cash_flow_type: string | null;
}

/**
 * Company-wide forecast P&L from historical patterns.
 *
 * Revenue streams (ChipChip GMV + fees, B2B, Grant income) and every
 * operating cost — split into COGS (Purchases + variable) and SG&A via the
 * shared pnl mapping — are each trend-projected over the next `HORIZON` months
 * from the trailing `LOOKBACK` complete months. Derived per month:
 *   CP1 = Revenue − Purchases
 *   Gross Profit / CP2 = CP1 − Variable Costs
 *   Operating Profit (EBIT) = CP2 − SG&A
 * Amounts in `displayCurrency`. Operating activities only (excludes
 * investing/financing outflows, taxes-on-profit and one-off items).
 */
export function useForecastPnl(displayCurrency: ForecastCurrency = 'ETB') {
  return useQuery({
    queryKey: ['forecast-pnl', displayCurrency],
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<ForecastPnlResult> => {
      const now = new Date();
      const basisStart = format(startOfMonth(subMonths(now, LOOKBACK)), 'yyyy-MM-dd');
      const basisEnd = format(endOfMonth(subMonths(now, 1)), 'yyyy-MM-dd');
      const basisMonths = Array.from({ length: LOOKBACK }, (_, i) => {
        const d = subMonths(now, LOOKBACK - i);
        return { key: format(d, 'yyyy-MM'), label: format(d, 'MMM yyyy') };
      });
      const basisKeys = new Set(basisMonths.map((m) => m.key));
      const forecastMonths = Array.from({ length: HORIZON }, (_, i) => {
        const d = addMonths(now, i);
        return { key: format(d, 'yyyy-MM'), label: format(d, 'MMM yyyy') };
      });
      // Wider window for expenses so quarterly/annual costs (taxes, licences) can
      // be recognised as periodic and amortized rather than trended from a spike.
      const expStart = format(startOfMonth(subMonths(now, EXP_LOOKBACK)), 'yyyy-MM-dd');
      const expMonths = Array.from({ length: EXP_LOOKBACK }, (_, i) =>
        format(subMonths(now, EXP_LOOKBACK - i), 'yyyy-MM')
      );
      const expKeys = new Set(expMonths);

      const [expsRes, catsRes, ratesRes, banksRes, ccSalesRes, ccFeesRes, b2bRes, inflowsRes] =
        await Promise.all([
          paginateSupabase<any>((from, to) =>
            (supabase as any)
              .from('expenses')
              .select('id,expense_date,amount,total_amount,net_payment,currency,category_id,bank_account_id')
              .in('payment_status', ['paid', 'partially_paid'])
              .gte('expense_date', expStart)
              .lte('expense_date', basisEnd)
              .range(from, to)
          ),
          (supabase as any)
            .from('expense_categories')
            .select('id,name,parent_id,category_type,cash_flow_type')
            .limit(10000),
          paginateSupabase<any>((from, to) =>
            (supabase as any)
              .from('exchange_rates')
              .select('currency_pair,rate,effective_date,bank_account_id,rate_type')
              .order('effective_date', { ascending: false })
              .range(from, to)
          ),
          (supabase as any).from('bank_accounts').select('id,exchange_rate_type').limit(10000),
          (supabase as any)
            .from('chipchip_daily_accepted_totals')
            .select('date,total_gmv')
            .gte('date', basisStart)
            .lte('date', basisEnd)
            .limit(10000),
          (supabase as any)
            .from('chipchip_daily_revenue_fees')
            .select('date,combined_total')
            .gte('date', basisStart)
            .lte('date', basisEnd)
            .limit(10000),
          // B2B: use the same source as this page's cash-flow statement
          // (credit collections), not b2b_sales_data which is sparse here.
          (supabase as any)
            .from('b2b_daily_credit_collections')
            .select('collection_date,credit_collected')
            .gte('collection_date', basisStart)
            .lte('collection_date', basisEnd)
            .limit(10000),
          // Grant income (Grantt BU) — treated as revenue, per the BU P&L.
          (supabase as any)
            .from('cash_inflows')
            .select('inflow_date,amount,currency,business_unit,bank_account_id,category')
            .gte('inflow_date', basisStart)
            .lte('inflow_date', basisEnd)
            .limit(10000),
        ]);

      const rates: RateRow[] = (ratesRes || []) as RateRow[];
      const toDisplay = (amount: number, ccy: string | null, date: string, bankAccountId?: string | null): number => {
        const from = (ccy || BASE_CCY).toUpperCase();
        let etb = amount;
        if (from !== BASE_CCY) {
          const c = convertAsOfForAccount(amount, from, BASE_CCY, date, rates, bankAccountId);
          etb = c ? c.converted : amount;
        }
        if (displayCurrency === 'ETB') return etb;
        const c2 = convertAsOfForAccount(etb, BASE_CCY, 'USD', date, rates, bankAccountId);
        return c2 ? c2.converted : etb;
      };

      const catMap = new Map<string, CategoryNode>();
      for (const c of catsRes.data || []) {
        catMap.set(c.id, {
          name: c.name,
          parent_id: c.parent_id,
          category_type: c.category_type,
          cash_flow_type: c.cash_flow_type,
        });
      }
      const groupLabel = (categoryId: string | null): string => {
        if (!categoryId) return 'Other Expenses';
        let cur = catMap.get(categoryId);
        while (cur) {
          if (cur.category_type === 'cost_group') return cur.name;
          if (cur.parent_id) {
            const parent = catMap.get(cur.parent_id);
            if (parent && parent.category_type === 'cost_group') return parent.name;
            cur = parent || undefined;
          } else break;
        }
        return catMap.get(categoryId)?.name || 'Other Expenses';
      };
      const positionLabel = (categoryId: string | null): string =>
        (categoryId && catMap.get(categoryId)?.name) || 'Uncategorized';
      const cashFlowType = (categoryId: string | null): string => {
        let cur = categoryId ? catMap.get(categoryId) : undefined;
        while (cur) {
          if (cur.cash_flow_type) return cur.cash_flow_type;
          cur = cur.parent_id ? catMap.get(cur.parent_id) : undefined;
        }
        return 'operating';
      };

      // Revenue series span the 6-month trend window; cost series span 12 months.
      const revSeries = () => Object.fromEntries(basisMonths.map((m) => [m.key, 0])) as Record<string, number>;
      const costSeries = () => Object.fromEntries(expMonths.map((k) => [k, 0])) as Record<string, number>;
      const revCc = revSeries();
      const revFees = revSeries();
      const revB2b = revSeries();
      const revGrant = revSeries();
      const cogs: Record<string, Record<string, number>> = {};
      for (const line of PNL_COGS_LINES) cogs[line] = costSeries();
      const sga: Record<string, Record<string, number>> = {};
      for (const line of [...PNL_SGA_LINES, OTHER_SGA]) sga[line] = costSeries();

      const bucket = (series: Record<string, number>, keySet: Set<string>, date: string, amt: number) => {
        const key = format(new Date(date), 'yyyy-MM');
        if (keySet.has(key)) series[key] += amt;
      };

      for (const r of ccSalesRes.data || []) bucket(revCc, basisKeys, r.date, toDisplay(Number(r.total_gmv || 0), 'ETB', r.date, null));
      for (const r of ccFeesRes.data || []) bucket(revFees, basisKeys, r.date, toDisplay(Number(r.combined_total || 0), 'ETB', r.date, null));
      for (const r of b2bRes.data || []) bucket(revB2b, basisKeys, r.collection_date, toDisplay(Number(r.credit_collected || 0), 'ETB', r.collection_date, null));
      for (const i of inflowsRes.data || []) {
        if (i.business_unit !== 'grantt' || !GRANT_INCOME_CATS.has(i.category)) continue;
        bucket(revGrant, basisKeys, i.inflow_date, toDisplay(Number(i.amount || 0), i.currency, i.inflow_date, i.bank_account_id));
      }

      for (const e of expsRes || []) {
        if (cashFlowType(e.category_id) !== 'operating') continue; // operating P&L only
        const amt = Number(e.net_payment ?? e.total_amount ?? e.amount ?? 0);
        if (!amt) continue;
        const converted = toDisplay(amt, e.currency, e.expense_date, e.bank_account_id);
        const cls = classifyExpenseToPnlLine(groupLabel(e.category_id), positionLabel(e.category_id));
        if (cls?.section === 'cogs') bucket(cogs[cls.line], expKeys, e.expense_date, converted);
        else if (cls?.section === 'sga') bucket(sga[cls.line], expKeys, e.expense_date, converted);
        else bucket(sga[OTHER_SGA], expKeys, e.expense_date, converted);
      }

      const idx = Array.from({ length: HORIZON }, (_, i) => i);
      // Revenue: trend over the 6-month window.
      const projRev = (s: Record<string, number>) => projectTrend(basisMonths.map((m) => s[m.key] || 0), HORIZON);
      // Cost: periodic lines (spend in few of the last 12 months — quarterly/annual
      // taxes, licences) are amortized evenly over 12 months so they aren't
      // trended from a one-off spike; steady lines follow their recent trend.
      const projCost = (s: Record<string, number>): number[] => {
        const ys12 = expMonths.map((k) => s[k] || 0);
        const active = ys12.filter((v) => Math.abs(v) > 0.005).length;
        if (active > 0 && active <= LUMPY_MAX_ACTIVE_MONTHS) {
          const monthly = Math.max(0, ys12.reduce((a, b) => a + b, 0) / EXP_LOOKBACK);
          return idx.map(() => monthly);
        }
        return projectTrend(ys12.slice(-LOOKBACK), HORIZON); // steady → recent trend
      };

      const fCc = projRev(revCc);
      const fFees = projRev(revFees);
      const fB2b = projRev(revB2b);
      const fGrant = projRev(revGrant);
      const fCogs: Record<string, number[]> = {};
      for (const line of PNL_COGS_LINES) fCogs[line] = projCost(cogs[line]);
      const fSga: Record<string, number[]> = {};
      for (const line of [...PNL_SGA_LINES, OTHER_SGA]) fSga[line] = projCost(sga[line]);

      const totalRev = idx.map((i) => fCc[i] + fFees[i] + fB2b[i] + fGrant[i]);
      const purchases = fCogs['Purchases'];
      const variableLines = PNL_COGS_LINES.slice(1);
      const variableTotal = idx.map((i) => variableLines.reduce((s, l) => s + fCogs[l][i], 0));
      const cp1 = idx.map((i) => totalRev[i] - purchases[i]);
      const cp2 = idx.map((i) => cp1[i] - variableTotal[i]);
      const sgaTotal = idx.map((i) => [...PNL_SGA_LINES, OTHER_SGA].reduce((s, l) => s + fSga[l][i], 0));
      const ebit = idx.map((i) => cp2[i] - sgaTotal[i]);
      const ratio = (num: number[]) => idx.map((i) => (totalRev[i] ? num[i] / totalRev[i] : 0));

      // Only render SG&A lines that have a non-trivial projection.
      const sgaRows = [...PNL_SGA_LINES, OTHER_SGA].filter((l) =>
        fSga[l].some((v) => Math.abs(v) > 0.005)
      );

      const rows: ForecastPnlRow[] = [
        { key: 'rev-header', label: 'REVENUE', kind: 'header', values: [] },
        { key: 'rev-cc', label: 'ChipChip', kind: 'sub', values: fCc },
        { key: 'rev-fees', label: 'ChipChip (Delivery & Packaging Fees)', kind: 'sub', values: fFees },
        { key: 'rev-b2b', label: 'B2B', kind: 'sub', values: fB2b },
        { key: 'rev-grant', label: 'Grant Income (Cost Reimbursement)', kind: 'sub', values: fGrant },
        { key: 'total-rev', label: 'Total Revenue', kind: 'total', values: totalRev },
        { key: 'purchases', label: 'Purchases', kind: 'cost', values: purchases },
        { key: 'var-header', label: 'VARIABLE COSTS', kind: 'header', values: [] },
        ...variableLines.map((l): ForecastPnlRow => ({ key: `var-${l}`, label: l, kind: 'cost', values: fCogs[l] })),
        { key: 'gross-profit', label: 'Gross Profit', kind: 'total', values: cp2 },
        { key: 'sga-header', label: 'OPERATING EXPENSES (SG&A)', kind: 'header', values: [] },
        ...sgaRows.map((l): ForecastPnlRow => ({ key: `sga-${l}`, label: l, kind: 'cost', values: fSga[l] })),
        { key: 'ebit', label: 'Operating Profit (EBIT)', kind: 'cp', values: ebit },
        { key: 'ebit-margin', label: 'EBIT Margin %', kind: 'margin', values: ratio(ebit) },
      ];

      // Horizon total per row: sum for value rows; blended CP/Revenue for margin
      // rows (summing percentages would be meaningless).
      const sum = (a: number[]) => a.reduce((s, v) => s + v, 0);
      const totalRevSum = sum(totalRev);
      const marginNum: Record<string, number[]> = {
        'ebit-margin': ebit,
      };
      const withTotals = rows.map((r) => {
        if (r.kind === 'header') return r;
        if (r.kind === 'margin') {
          return { ...r, total: totalRevSum ? sum(marginNum[r.key] || []) / totalRevSum : 0 };
        }
        return { ...r, total: sum(r.values) };
      });

      return { basisMonths, forecastMonths, rows: withTotals };
    },
  });
}
